import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import { type MouseEvent, type ComponentType } from "react";
import { Link, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { type IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { SuperKuduChip } from "../SuperKuduChip";
import { TOOLTIP_STYLES } from "./constants";
import { type NavigationIcon } from "./sidebarUtils";

interface NavigationItem {
  label: string;
  href: string;
  icon?: NavigationIcon;
  activeMatcher?: (path: string, search: string) => boolean;
  description?: string;
  onClick?: () => void;
  variant?: "default" | "highlight";
  id?: string;
  superKuduOnly?: boolean;
}

interface NavigationItemProps {
  item: NavigationItem;
  isExpanded: boolean;
  showTooltip?: boolean;
}

function renderIcon(icon: NavigationItem["icon"]) {
  if (!icon) return null;

  if (typeof icon === "object" && "iconName" in icon) {
    return (
      <FontAwesomeIcon
        icon={icon as IconDefinition}
        style={{ width: "20px", height: "20px" }}
      />
    );
  }

  if (typeof icon === "string") {
    return <img src={icon} alt="" style={{ width: "20px", height: "20px" }} />;
  }

  if (typeof icon === "function") {
    const IconComponent = icon as ComponentType<Record<string, never>>;
    return <IconComponent />;
  }

  return null;
}

export function NavigationItem({
  item,
  isExpanded,
  showTooltip = true,
}: NavigationItemProps) {
  const location = useLocation();
  const isActive = item.activeMatcher
    ? item.activeMatcher(location.pathname, location.search)
    : location.pathname === item.href;
  const isHighlight = item.variant === "highlight";

  const handleClick = (e: MouseEvent) => {
    if (item.onClick) {
      e.preventDefault();
      item.onClick();
    }
  };

  const iconElement = item.icon ? (
    <Box
      sx={{
        width: "20px",
        height: "20px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        "& svg": {
          width: "20px",
          height: "20px",
          color: isHighlight ? "#0046a0" : isActive ? "#0046a0" : "#1f1f1f",
        },
      }}
    >
      {renderIcon(item.icon)}
    </Box>
  ) : null;

  const labelElement = isExpanded ? (
    <Box
      component="span"
      sx={{
        fontSize: "14px",
        fontWeight: 500,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        lineHeight: 1,
        color: isHighlight ? "#0046a0" : isActive ? "#0046a0" : "#1f1f1f",
      }}
    >
      {item.label}
    </Box>
  ) : null;

  const itemSx = {
    display: "flex",
    alignItems: "center",
    padding: "8px 12px",
    textDecoration: "none",
    borderRadius: "6px",
    fontSize: "14px",
    gap: "12px",
    transition: "background-color 150ms ease",
    color: isHighlight ? "#0046a0" : isActive ? "#0046a0" : "#1f1f1f",
    backgroundColor: isHighlight
      ? "rgba(0, 70, 160, 0.05)"
      : isActive
        ? "rgba(0, 70, 160, 0.1)"
        : "transparent",
    "&:hover": {
      backgroundColor: isHighlight
        ? "rgba(0, 70, 160, 0.05)"
        : isActive
          ? "rgba(0, 70, 160, 0.1)"
          : "#f9fafb",
    },
  };

  const isExternalUrl =
    item.href.startsWith("http://") || item.href.startsWith("https://");

  // When collapsed, show both label and description in tooltip
  // When expanded, show description if available, otherwise label
  const tooltipContent =
    !isExpanded && item.description ? (
      <Box>
        <Box component="div" sx={{ fontWeight: 600, marginBottom: "4px" }}>
          {item.label}
        </Box>
        <Box component="div" sx={{ fontSize: "13px", opacity: 0.9 }}>
          {item.description}
        </Box>
      </Box>
    ) : (
      item.description || item.label
    );

  const shouldShowTooltip = showTooltip && item.label !== "Back Home";

  const itemContent = (
    <>
      {iconElement}
      {labelElement}
      {isExpanded && item.superKuduOnly && (
        <SuperKuduChip data-testid={item.id && `${item.id}-superkudu-chip`} />
      )}
    </>
  );

  const itemElement = item.onClick ? (
    <Box
      id={item.id}
      component="button"
      onClick={handleClick}
      sx={{
        ...itemSx,
        border: "none",
        cursor: "pointer",
        width: "100%",
      }}
    >
      {itemContent}
    </Box>
  ) : isExternalUrl ? (
    <Box
      id={item.id}
      component="a"
      href={item.href}
      target="_blank"
      rel="noopener noreferrer"
      sx={itemSx}
    >
      {itemContent}
    </Box>
  ) : (
    <Box id={item.id} component={Link} to={item.href} sx={itemSx}>
      {itemContent}
    </Box>
  );

  if (shouldShowTooltip) {
    return (
      <Tooltip
        title={tooltipContent}
        placement="right"
        enterDelay={300}
        leaveDelay={0}
        slotProps={TOOLTIP_STYLES}
      >
        {itemElement}
      </Tooltip>
    );
  }

  return itemElement;
}
